import { useEffect, useRef, useState } from 'react';
import { createEmptyTaskCaseDetailView, emptyCriminalCaseDetail, type TaskCaseDetailView } from '../data/taskCaseDetails';
import { getCaseRequestFileMovementLogs, getCaseRequestHeader, getCaseRequestWorkflowProgress } from '../services/caseRequestDetails';
import type { CaseRequestHeaderResponse, CaseRequestWorkflowProgressResponse } from '../types/caseRequestDetails';
import {
  loadCaseRequestViewDataFromHeader,
  type CaseRequestViewPayload,
} from '../services/caseRequestViewData';
import {
  custodyPillClass,
  formatSubmittedAt,
  headerCategory,
  headerCurrentStage,
  headerIsReceivedViaForward,
  headerLinkedFileRef,
  headerPhysicalFileCustody,
  headerReferenceNo,
  headerSubCategory,
  headerTaskCreatedById,
  headerTaskCreatedByName,
  headerTaskStatus,
  headerTaskType,
  isClosureStage,
  isRejectedTaskStatus,
  mapCriminalViewToCaseDetail,
  isCurrentWorkflowStageClosure,
  isNextWorkflowStageClosure,
  mapWorkflowStagesToSteps,
  statusPillClass,
} from '../utils/taskCaseDetailMappers';
import type { WizardFlowKind } from '../utils/wizardCategoryRoute';
import { wizardFlowForCategoryName } from '../utils/wizardCategoryRoute';
import { isCaseRequestUuid } from '../pages/task-case/taskCaseUpdateHelpers';
import { formatDisplayDateTime } from '../utils/dateFormat';
import { toUserFacingErrorMessage } from '../utils/userFacingError';

export type TaskCaseDetailsTabId =
  | 'cd'
  | 'cu'
  | 'docs'
  | 'comm'
  | 'min'
  | 'ltr'
  | 'st'
  | 'fml'
  | 'al'
  | 'inv';

export type TaskCaseDetailsLoadState = {
  loading: boolean;
  error: string;
  detail: TaskCaseDetailView;
  caseRequestId: string;
  /** Canonical case request id from steps API (extranet: draft.draftRequestId). */
  resolvedCaseRequestId: string;
  userTaskId: string;
  flow: WizardFlowKind;
  /**
   * Original submitted snapshot (`?view=original`) — Case Details tab only.
   * Never populated from latest / Case Update APIs.
   */
  viewPayload: CaseRequestViewPayload | null;
  /** True while Case Details (`view=original`) is loading or refreshing. */
  viewPayloadLoading: boolean;
  /** Set when `view=original` fails or returns empty — Case Details must not show other API data. */
  caseDetailsError: string;
  reload: () => void;
};

function criminalViewFromPayload(payload: CaseRequestViewPayload | null) {
  if (payload?.flow === 'criminal') return payload.data;
  return null;
}

function draftRequestIdFromPayload(payload: CaseRequestViewPayload | null): string {
  if (!payload) return '';
  return payload.data.draft.draftRequestId?.trim() || '';
}

function mapFileMovementEntries(
  fmlRes: Awaited<ReturnType<typeof getCaseRequestFileMovementLogs>>,
): TaskCaseDetailView['fileMovement'] {
  if (!fmlRes.success) return [];
  const entries = Array.isArray(fmlRes.data?.entries) ? fmlRes.data.entries : [];
  return entries
    .filter((e) => e && typeof e === 'object')
    .map((e) => {
      const raw = e as Record<string, unknown>;
      const createdAt =
        (typeof raw.createdAt === 'string' ? raw.createdAt : '') ||
        (typeof raw.created_at === 'string' ? raw.created_at : '');
      const dt = formatDisplayDateTime(createdAt);
      const [date, time] = dt.includes(',') ? dt.split(',').map((s) => s.trim()) : [dt, ''];

      const isCheckedIn = raw.isCheckedIn === true || raw.is_checked_in === true;
      const event = isCheckedIn ? ('qrin' as const) : ('qrout' as const);

      const name =
        (typeof raw.userDisplayName === 'string' ? raw.userDisplayName : '') ||
        (typeof raw.user_display_name === 'string' ? raw.user_display_name : '') ||
        '—';
      const role =
        (typeof raw.userRole === 'string' ? raw.userRole : '') ||
        (typeof raw.user_role === 'string' ? raw.user_role : '');
      const division =
        (typeof raw.userDivision === 'string' ? raw.userDivision : '') ||
        (typeof raw.user_division === 'string' ? raw.user_division : '');
      const title =
        (typeof raw.userTitle === 'string' ? raw.userTitle : '') ||
        (typeof raw.user_title === 'string' ? raw.user_title : '');

      const roleLine =
        [role, division].filter((v) => v && String(v).trim()).join(' · ').trim() || title.trim() || '—';

      const note = isCheckedIn ? 'QR Check-In recorded' : 'QR Check-Out recorded';

      return {
        date: date || '—',
        time: time || '—',
        event,
        name,
        role: roleLine,
        note,
      };
    });
}

function buildDetailFromApis(
  header: CaseRequestHeaderResponse,
  workflow: CaseRequestWorkflowProgressResponse | undefined,
  viewPayload: CaseRequestViewPayload | null,
  fileMovement: TaskCaseDetailView['fileMovement'],
): TaskCaseDetailView {
  const empty = createEmptyTaskCaseDetailView();
  const criminal = criminalViewFromPayload(viewPayload);
  const draft = viewPayload?.data.draft;
  const taskStatus = headerTaskStatus(header);
  const currentStage = headerCurrentStage(header);
  const taskType = headerTaskType(header);
  const rejected = isRejectedTaskStatus(taskStatus);
  const workflowSteps = workflow?.stages?.length ? mapWorkflowStagesToSteps(workflow.stages) : [];
  const currentStageIsClosure = workflow?.stages?.length
    ? isCurrentWorkflowStageClosure(workflow.stages)
    : isClosureStage(currentStage);
  const nextStageIsClosure = workflow?.stages?.length
    ? isNextWorkflowStageClosure(workflow.stages)
    : false;
  const custody = headerPhysicalFileCustody(header);
  const submittedAtRaw = viewPayload?.data.submittedAt;

  return {
    ...empty,
    variant: rejected ? 'rejected' : currentStageIsClosure ? 'closure' : 'active',
    fileCategoryId: draft?.fileCategoryId?.trim() || '',
    subFileCategoryId: draft?.subFileCategoryId?.trim() || '',
    taskType,
    isReceivedViaForward: headerIsReceivedViaForward(header),
    taskCreatedById: headerTaskCreatedById(header),
    taskCreatedByName: headerTaskCreatedByName(header),
    fileRef: headerReferenceNo(header),
    fileCategory: headerCategory(header),
    subFileCategory: headerSubCategory(header),
    currentStage,
    taskStatus,
    taskStatusClass: statusPillClass(taskStatus),
    custodyStatus: custody,
    custodyClass: custodyPillClass(custody),
    linkedFileRef: headerLinkedFileRef(header),
    showWorkflow: !rejected && workflowSteps.length > 0,
    showWorkflowActions: !rejected,
    workflowActionMode: currentStageIsClosure ? 'complete' : 'submit',
    currentStageIsClosure,
    nextStageIsClosure,
    workflowSteps,
    submittedAt: submittedAtRaw ? formatSubmittedAt(submittedAtRaw) : '',
    recentSubmittedAt: submittedAtRaw ? formatSubmittedAt(submittedAtRaw) : '',
    case: criminal ? mapCriminalViewToCaseDetail(criminal) : emptyCriminalCaseDetail(),
    rejection: undefined,
    fileMovement,
  };
}

const CASE_DETAILS_LOAD_FAILED = 'Failed to load original case details.';
const CASE_DETAILS_UNAVAILABLE = 'Original case details are not available.';

function caseDetailsLoadErrorMessage(apiMessage?: string | null): string {
  return toUserFacingErrorMessage(apiMessage, CASE_DETAILS_LOAD_FAILED);
}

/**
 * Loads page shell (header + workflow) and Case Details (`view=original`) only.
 * Case Update (latest) is never used as a fallback for Case Details.
 */
export function useTaskCaseDetailsData(
  caseRequestId: string,
  userTaskId: string,
  activeTab: TaskCaseDetailsTabId = 'cd',
): TaskCaseDetailsLoadState {
  const useLiveApi = isCaseRequestUuid(caseRequestId);

  const [loading, setLoading] = useState(useLiveApi);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState<TaskCaseDetailView>(createEmptyTaskCaseDetailView);
  const [resolvedCaseRequestId, setResolvedCaseRequestId] = useState(caseRequestId);
  const [flow, setFlow] = useState<WizardFlowKind>('unknown');
  const [viewPayload, setViewPayload] = useState<CaseRequestViewPayload | null>(null);
  const [viewPayloadLoading, setViewPayloadLoading] = useState(false);
  const [caseDetailsError, setCaseDetailsError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const headerRef = useRef<CaseRequestHeaderResponse | null>(null);
  const workflowRef = useRef<CaseRequestWorkflowProgressResponse | undefined>(undefined);
  const fileMovementRef = useRef<TaskCaseDetailView['fileMovement']>([]);
  const prevTabRef = useRef<TaskCaseDetailsTabId>(activeTab);
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  const reload = () => setReloadKey((n) => n + 1);

  // Page shell: header + workflow. Case Details uses only `?view=original` (no latest fallback).
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect -- reset + load on case/task change */
    setDetail(createEmptyTaskCaseDetailView());
    setResolvedCaseRequestId(caseRequestId);
    setFlow('unknown');
    setViewPayload(null);
    setCaseDetailsError('');
    setViewPayloadLoading(false);
    headerRef.current = null;
    workflowRef.current = undefined;
    fileMovementRef.current = [];
    prevTabRef.current = activeTabRef.current;

    if (!useLiveApi) {
      setLoading(false);
      setError('Invalid case request id.');
      return;
    }

    let mounted = true;
    setLoading(true);
    setError('');

    const workflowUserTaskId = userTaskId.trim();
    const workflowPromise =
      workflowUserTaskId && isCaseRequestUuid(workflowUserTaskId)
        ? getCaseRequestWorkflowProgress(workflowUserTaskId)
        : Promise.resolve({ success: false as const, message: 'User task id is missing.' });

    void (async () => {
      const headerRes = await getCaseRequestHeader(caseRequestId, workflowUserTaskId);
      if (!mounted) return;

      if (!headerRes.success) {
        setError(headerRes.message ?? 'Failed to load case request header.');
        setLoading(false);
        return;
      }

      const workflowRes = await workflowPromise;
      if (!mounted) return;

      const resolvedFlow = wizardFlowForCategoryName(headerCategory(headerRes.data));
      headerRef.current = headerRes.data;
      workflowRef.current = workflowRes.success ? workflowRes.data : undefined;

      setFlow(resolvedFlow);
      setResolvedCaseRequestId(caseRequestId.trim());
      setDetail(
        buildDetailFromApis(
          headerRes.data,
          workflowRef.current,
          null,
          fileMovementRef.current,
        ),
      );
      setLoading(false);

      if (activeTabRef.current !== 'cd') return;

      setViewPayloadLoading(true);
      const originalViewRes = await loadCaseRequestViewDataFromHeader(caseRequestId, headerRes.data, {
        view: 'original',
      });
      if (!mounted) return;

      if (!originalViewRes.success || !originalViewRes.data.payload) {
        setViewPayload(null);
        setCaseDetailsError(
          originalViewRes.success
            ? CASE_DETAILS_UNAVAILABLE
            : caseDetailsLoadErrorMessage(originalViewRes.message),
        );
        setViewPayloadLoading(false);
        return;
      }

      const originalPayload = originalViewRes.data.payload;
      const flowFromOriginal =
        originalViewRes.data.flow !== 'unknown' ? originalViewRes.data.flow : resolvedFlow;

      setFlow(flowFromOriginal);
      setCaseDetailsError('');
      setViewPayload(originalPayload);
      setResolvedCaseRequestId(
        draftRequestIdFromPayload(originalPayload) || caseRequestId.trim(),
      );
      setDetail(
        buildDetailFromApis(
          headerRes.data,
          workflowRef.current,
          originalPayload,
          fileMovementRef.current,
        ),
      );
      setViewPayloadLoading(false);
    })();

    return () => {
      mounted = false;
    };
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [caseRequestId, userTaskId, useLiveApi, reloadKey]);

  // Refresh Case Details from `view=original` only when switching back to that tab.
  useEffect(() => {
    const prevTab = prevTabRef.current;
    prevTabRef.current = activeTab;

    if (!useLiveApi || activeTab !== 'cd' || prevTab === 'cd') return;

    const header = headerRef.current;
    if (!header) return;

    let mounted = true;
    setViewPayloadLoading(true);
    // Clear immediately so a failed original never leaves stale / other-API data visible.
    setViewPayload(null);
    setCaseDetailsError('');

    void (async () => {
      const originalViewRes = await loadCaseRequestViewDataFromHeader(caseRequestId, header, {
        view: 'original',
      });
      if (!mounted) return;

      if (!originalViewRes.success || !originalViewRes.data.payload) {
        setViewPayload(null);
        setCaseDetailsError(
          originalViewRes.success
            ? CASE_DETAILS_UNAVAILABLE
            : caseDetailsLoadErrorMessage(originalViewRes.message),
        );
        setViewPayloadLoading(false);
        return;
      }

      const originalPayload = originalViewRes.data.payload;
      const flowFromOriginal =
        originalViewRes.data.flow !== 'unknown'
          ? originalViewRes.data.flow
          : wizardFlowForCategoryName(headerCategory(header));

      setFlow(flowFromOriginal);
      setCaseDetailsError('');
      setViewPayload(originalPayload);
      setResolvedCaseRequestId(
        draftRequestIdFromPayload(originalPayload) || caseRequestId.trim(),
      );
      setDetail(
        buildDetailFromApis(header, workflowRef.current, originalPayload, fileMovementRef.current),
      );
      setViewPayloadLoading(false);
    })();

    return () => {
      mounted = false;
    };
  }, [activeTab, caseRequestId, useLiveApi]);

  // Load File Movement Log only when that tab is selected (fresh each visit).
  useEffect(() => {
    if (!useLiveApi || activeTab !== 'fml') return;

    let mounted = true;
    void (async () => {
      const fmlRes = await getCaseRequestFileMovementLogs(caseRequestId);
      if (!mounted) return;
      const fileMovement = mapFileMovementEntries(fmlRes);
      fileMovementRef.current = fileMovement;
      setDetail((prev) => ({ ...prev, fileMovement }));
    })();

    return () => {
      mounted = false;
    };
  }, [activeTab, caseRequestId, useLiveApi, reloadKey]);

  return {
    loading,
    error,
    detail,
    caseRequestId,
    resolvedCaseRequestId,
    userTaskId: userTaskId.trim(),
    flow,
    viewPayload,
    viewPayloadLoading,
    caseDetailsError,
    reload,
  };
}
